import { forwardRef } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Slide, { SlideProps } from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import { useReducedMotion } from 'utils/motion';

const SlideUp = forwardRef<HTMLDivElement, SlideProps>((props, ref) => (
  <Slide {...props} direction="up" ref={ref} />
));
SlideUp.displayName = 'SlideUp';

interface Props {
  open: boolean;
  message: string;
  /** One action on the pill, e.g. „Rückgängig“ after hiding something. */
  action?: { label: string; onClick: () => void };
}

/**
 * The app's single confirmation toast: a dark pill that slides up from the
 * bottom-centre. Used for every "action with a consequence" acknowledgement.
 * Slide is suppressed under reduced-motion.
 */
export const ToastPill = ({ open, message, action }: Props) => {
  const reduced = useReducedMotion();

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={SlideUp}
      transitionDuration={reduced ? 0 : undefined}
      sx={{ bottom: { xs: 32 } }}
    >
      <Box
        role="status"
        aria-live="polite"
        sx={{
          bgcolor: 'grey.900',
          color: 'common.white',
          px: 2.5,
          py: 1.25,
          borderRadius: '999px',
          fontWeight: 600,
          fontSize: 14,
          lineHeight: 1.4,
          textAlign: 'center',
          maxWidth: '90vw',
          boxShadow: theme => theme.customShadows?.z24 ?? theme.shadows[12]
        }}
      >
        {message}
        {action && (
          <ButtonBase
            onClick={action.onClick}
            data-testid="toast-action"
            sx={{
              ml: 1.5,
              color: 'primary.light',
              fontWeight: 700,
              fontSize: 14,
              textDecoration: 'underline'
            }}
          >
            {action.label}
          </ButtonBase>
        )}
      </Box>
    </Snackbar>
  );
};
